import { Injectable } from "@angular/core";
import { ServiceResponse } from "@appcore/data";
import { API } from "@service/system/api.service";
import { HttpManager } from "@service/system/httpManager.service";
import { ResponseHandler } from "@service/system/responseHandler.service";
import { environment } from "environments/environment";
import { Observable } from "rxjs";

const API_URL = API.API_URL;
const _shareName: string = environment.memberShareName;

@Injectable()
export class AzureFileShareService {
	private apiURL = `${API_URL}/storage/azure/fileshare`;
	private fileShareCopy = `${this.apiURL}/copy`;
	private fileShareDelete = `${this.apiURL}/delete`;
	private fileShareGet = `${this.apiURL}`;
	private fileShareMove = `${this.apiURL}/move`;
	private fileShareRename = `${this.apiURL}/rename`;
	private fileShareUpload = `${this.apiURL}/upload`;

	constructor(private httpManager: HttpManager, private responseHandler: ResponseHandler) {}

	copyFileShareObject(
		shareName: string = _shareName,
		currentPath: string,
		newPath: string,
		deleteOld: boolean = true
	): Observable<ServiceResponse<CopyFileShareResponse[] | CopyDirectoryShareResponse[]>> {
		return this.responseHandler.httpGet<CopyFileShareResponse[] | CopyDirectoryShareResponse[]>(
			`${this.fileShareCopy}`,
			{ shareName: shareName, currentPath: currentPath, newPath: newPath, deleteOld: deleteOld }
		);
	}

	deleteFileShareObject(shareName: string = _shareName, path: string): Observable<ServiceResponse<[]>> {
		return this.responseHandler.httpGet<[]>(`${this.fileShareDelete}`, {
			shareName: shareName,
			path: path
		});
	}

	getFileShareFiles(
		shareName: string = _shareName,
		subFolder: string
	): Observable<ServiceResponse<GetFileShareResponse>> {
		return this.responseHandler.httpGet<GetFileShareResponse>(`${this.fileShareGet}`, {
			shareName: shareName,
			subFolder: subFolder
		});
	}

	moveFileShareFolder(
		shareName: string = _shareName,
		originalFolderPath: string,
		destinationFolderPath: string
	): Observable<ServiceResponse<MoveFileShareResponse[] | string>> {
		return this.responseHandler.httpGet<MoveFileShareResponse[] | string>(`${this.fileShareMove}`, {
			shareName: shareName,
			originalFolderPath: originalFolderPath,
			destinationFolderPath: destinationFolderPath
		});
	}

	renameFileSharePath(
		shareName: string = _shareName,
		currentPath: string,
		newPath: string
	): Observable<ServiceResponse<RenameFileShareResponse[]>> {
		return this.responseHandler.httpGet<RenameFileShareResponse[]>(`${this.fileShareRename}`, {
			shareName: shareName,
			currentPath: currentPath,
			newPath: newPath
		});
	}

	uploadFilesToFileShare(destinationFolder: string, body: any): Observable<ServiceResponse<string>> {
		return this.httpManager.httpPostMediaFile<any>(`${this.fileShareUpload}`, body, {
			shareName: _shareName,
			destinationFolder: destinationFolder
		});
	}
}

export interface MediaFile {
	type?: string;
	filename?: string;
	path?: string;
	datecreated?: string;
	datemodified?: string;
}

export interface AzFsUploadBody {
	sourcePath: string;
	mediaFolderString: string;
	files: MediaFile;
	fileOnly: boolean;
}

export interface CopyFileShareResponse {
	CopyId?: string;
	CopyStatus?: unknown;
	ETag?: unknown;
	LastModified?: unknown;
}

export interface CopyDirectoryShareResponse {
	AccountName?: string;
	ClientConfiguration?: unknown;
	DirectoryRestClient?: unknown;
	Name?: string;
	Path?: string;
	ShareName?: string;
	Uri?: unknown;
}

export interface DeleteFileShareDeleteResponse {
	ReasonPhrase?: string;
	Status?: number;
}

export interface GetFileShareResponse {
	parentPathName?: string;
	contents?: FileShareContents;
}

export interface FileShareContents {
	datecreated?: string;
	datemodified?: string;
	name?: string;
	path?: string;
	type?: string;
}

export interface MoveFileShareResponse {
	CopyId?: string;
	CopyStatus?: unknown;
	ETag?: unknown;
	LastModified?: unknown;
}

export interface RenameFileShareResponse {
	AccountName?: string;
	ClientConfiguration?: unknown;
	DirectoryRestClient?: unknown;
	Name?: string;
	Path?: string;
	ShareName?: string;
	Uri?: unknown;
}
